/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  Gauge,
  Globe,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Settings2,
  ShieldCheck,
  Timer,
  XCircle,
  Zap,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";

const tenantDb = supabase as any;

const FREQ_OPTIONS = [
  { value: "1h", label: "Hourly", hours: 1 },
  { value: "2h", label: "Every 2 hours", hours: 2 },
  { value: "3h", label: "Every 3 hours", hours: 3 },
  { value: "4h", label: "Every 4 hours", hours: 4 },
  { value: "6h", label: "Every 6 hours", hours: 6 },
  { value: "8h", label: "Every 8 hours", hours: 8 },
  { value: "12h", label: "Every 12 hours", hours: 12 },
  { value: "24h", label: "1 article/day", hours: 24 },
  { value: "48h", label: "Every 2 days", hours: 48 },
  { value: "168h", label: "Weekly", hours: 168 },
];

const trialDemoAutomationSites: SiteRow[] = [
  {
    id: "trial-demo-auto-site",
    name: "Demo City Journal",
    domain: "demo-city.delapresse.site",
    automation_config: { enabled: true, autoPublish: true, frequency: "6h", intervalHours: 6, publishWithoutAiRewrite: true },
    content_config: { niche: "Local news and business" },
    status: "active",
  },
  {
    id: "trial-demo-auto-business",
    name: "Demo Business Desk",
    domain: "business-demo.delapresse.site",
    automation_config: { enabled: false, autoPublish: false, frequency: "24h", intervalHours: 24, publishWithoutAiRewrite: true },
    content_config: { niche: "Business analysis" },
    status: "active",
  },
];

const trialDemoAutomationJobs: JobRow[] = [
  {
    id: "trial-demo-job-queued",
    website_id: "trial-demo-auto-site",
    article_id: "trial-demo-article",
    status: "queued",
    scheduled_for: new Date(Date.now() + 1000 * 60 * 45).toISOString(),
    created_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    completed_at: null,
    error_message: null,
    attempts: 0,
    max_attempts: 3,
    next_run_at: new Date(Date.now() + 1000 * 60 * 45).toISOString(),
    progress: 15,
    health: "queued",
    trigger_type: "scheduled",
  },
  {
    id: "trial-demo-job-complete",
    website_id: "trial-demo-auto-site",
    article_id: "trial-demo-article-2",
    status: "completed",
    scheduled_for: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
    created_at: new Date(Date.now() - 1000 * 60 * 220).toISOString(),
    completed_at: new Date(Date.now() - 1000 * 60 * 170).toISOString(),
    error_message: null,
    attempts: 1,
    max_attempts: 3,
    last_run_at: new Date(Date.now() - 1000 * 60 * 170).toISOString(),
    duration_ms: 42000,
    progress: 100,
    health: "healthy",
    trigger_type: "manual",
  },
];

type SiteRow = {
  id: string;
  name: string;
  domain: string | null;
  automation_config: Record<string, any> | null;
  content_config: Record<string, any> | null;
  status: string;
};

type JobRow = {
  id: string;
  website_id: string;
  article_id: string | null;
  status: string;
  scheduled_for: string;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  attempts?: number;
  max_attempts?: number;
  started_at?: string | null;
  next_run_at?: string | null;
  last_run_at?: string | null;
  duration_ms?: number | null;
  progress?: number;
  health?: string;
  trigger_type?: string;
  input_data?: Record<string, any>;
  output_data?: Record<string, any>;
  last_success_at?: string | null;
  last_failure_at?: string | null;
  failure_reason?: string | null;
  notification_sent_at?: string | null;
  notification_type?: string | null;
  missed_run_at?: string | null;
};

type RunRow = {
  id: string;
  job_id: string;
  website_id: string;
  article_id: string | null;
  status: string;
  trigger_type: string;
  attempt: number;
  input_data: Record<string, any>;
  output_data: Record<string, any>;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  created_at: string;
};

const normalizeStatus = (status: string) => {
  if (status === "generating") return "running";
  if (status === "published" || status === "draft") return "completed";
  return status;
};

const formatDuration = (ms?: number | null) => {
  if (!ms) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
};

const getFreqHours = (config: Record<string, any>) => {
  if (Number(config.intervalHours) > 0) return Number(config.intervalHours);
  return FREQ_OPTIONS.find((f) => f.value === config.frequency)?.hours || 24;
};

const getProgressValue = (job?: JobRow) => {
  if (!job) return 0;
  const normalized = normalizeStatus(job.status);
  if (normalized === "completed" || normalized === "failed") return 100;
  return Math.max(0, Math.min(100, Number(job.progress || 0)));
};

const getLatestHealth = (job: JobRow | undefined, enabled: boolean, stalled: boolean, missed = false) => {
  if (!enabled) return "paused";
  if (!job) return "idle";
  const normalized = normalizeStatus(job.status);
  if (stalled || missed || normalized === "failed") return "failed";
  if (normalized === "queued") return "queued";
  if (normalized === "running") return "running";
  if (job.health === "warning" || job.error_message) return "warning";
  return "healthy";
};

const statusMeta = (status: string) => {
  const normalized = normalizeStatus(status);
  if (normalized === "completed") return { icon: CheckCircle2, label: "Completed", className: "bg-success/10 text-success" };
  if (normalized === "running") return { icon: Loader2, label: "Running", className: "bg-info/10 text-info" };
  if (normalized === "failed") return { icon: XCircle, label: "Failed", className: "bg-destructive/10 text-destructive" };
  return { icon: Clock, label: "Queued", className: "bg-warning/10 text-warning" };
};

export default function AutoPublishPage() {
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const trialMode = useTrialMode();
  const queryClient = useQueryClient();
  const [pausingAll, setPausingAll] = useState(false);
  const [runningEngine, setRunningEngine] = useState(false);
  const [triggeringSite, setTriggeringSite] = useState<string | null>(null);
  const [retryingJob, setRetryingJob] = useState<string | null>(null);
  const [customFrequencySites, setCustomFrequencySites] = useState<Set<string>>(new Set());
  const [runStatusFilter, setRunStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data: websites = [], isLoading: loadingWebsites } = useQuery({
    queryKey: ["websites-autopublish", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await tenantDb
        .from("websites")
        .select("id, name, domain, automation_config, content_config, status")
        .eq("company_id", currentCompanyId)
        .order("name");
      if (error) throw error;
      return (data || []) as SiteRow[];
    },
    enabled: !!currentCompanyId,
    refetchInterval: 30000,
  });

  const { data: jobs = [], isLoading: loadingJobs } = useQuery({
    queryKey: ["autopublish-jobs", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await tenantDb
        .from("autopublish_jobs")
        .select("*")
        .eq("company_id", currentCompanyId)
        .order("created_at", { ascending: false })
        .limit(250);
      if (error) throw error;
      return (data || []) as unknown as JobRow[];
    },
    refetchInterval: 5000,
  });

  const { data: runs = [], isLoading: loadingRuns } = useQuery({
    queryKey: ["autopublish-job-runs", currentCompanyId, runStatusFilter, jobFilter, fromDate, toDate],
    queryFn: async () => {
      let query = (supabase as any)
        .from("autopublish_job_runs")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(200);
      if (runStatusFilter !== "all") query = query.eq("status", runStatusFilter);
      if (jobFilter !== "all") query = query.eq("job_id", jobFilter);
      if (fromDate) query = query.gte("started_at", new Date(fromDate).toISOString());
      if (toDate) query = query.lte("started_at", new Date(`${toDate}T23:59:59`).toISOString());
      if (currentCompanyId && websites.length > 0) {
        query = query.in("website_id", websites.map((site) => site.id));
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as RunRow[];
    },
    enabled: !!currentCompanyId,
    refetchInterval: 5000,
  });

  const getSite = (wid: string) => automationWebsites.find((w) => w.id === wid);
  const getSiteName = (wid: string) => getSite(wid)?.name || "Unknown site";
  const automationWebsites = trialMode.isReadOnly && websites.length === 0 ? trialDemoAutomationSites : websites;
  const automationJobs = trialMode.isReadOnly && jobs.length === 0 ? trialDemoAutomationJobs : jobs;
  const blockTrialAction = () => toast.error(TRIAL_READ_ONLY_MESSAGE);

  const stats = useMemo(() => {
    const normalizedJobs = automationJobs.map((j) => ({ ...j, normalized: normalizeStatus(j.status) }));
    const completed = normalizedJobs.filter((j) => j.normalized === "completed");
    const failed = normalizedJobs.filter((j) => j.normalized === "failed");
    const queued = normalizedJobs.filter((j) => j.normalized === "queued");
    const running = normalizedJobs.filter((j) => j.normalized === "running");
    const executed = completed.length + failed.length;
    const successRate = executed ? Math.round((completed.length / executed) * 100) : 0;
    const avgDuration = completed
      .filter((j) => j.duration_ms)
      .reduce((sum, j, _, arr) => sum + Number(j.duration_ms || 0) / arr.length, 0);
    const stalled = automationJobs.filter((j) => {
      const started = j.started_at ? new Date(j.started_at).getTime() : 0;
      return normalizeStatus(j.status) === "running" && started && Date.now() - started > 20 * 60 * 1000;
    });
    const repeatedFailures = automationWebsites.filter((site) => automationJobs.filter((j) => j.website_id === site.id && normalizeStatus(j.status) === "failed").length >= 2);
    const oldQueued = automationJobs.filter((j) => normalizeStatus(j.status) === "queued" && new Date(j.scheduled_for).getTime() < Date.now() - 15 * 60 * 1000);
    const lastRunAt = normalizedJobs
      .map((j) => j.completed_at || j.last_run_at || j.created_at)
      .filter(Boolean)
      .map((value) => new Date(value as string).getTime())
      .sort((a, b) => b - a)[0] || null;
    const stopped = Boolean(lastRunAt && Date.now() - lastRunAt > 24 * 60 * 60 * 1000 && automationWebsites.some((site) => site.automation_config?.enabled || site.automation_config?.autoPublish));
    return { completed, failed, queued, running, executed, successRate, avgDuration, stalled, repeatedFailures, oldQueued, lastRunAt, stopped };
  }, [automationJobs, automationWebsites]);

  const siteSummaries = useMemo(() => automationWebsites.map((site) => {
    const siteJobs = automationJobs.filter((j) => j.website_id === site.id);
    const latest = siteJobs[0];
    const config = site.automation_config || {};
    const enabled = config.enabled === true || config.autoPublish === true;
    const stalled = latest?.started_at && normalizeStatus(latest.status) === "running" && Date.now() - new Date(latest.started_at).getTime() > 20 * 60 * 1000;
    const progress = getProgressValue(latest);
    const nextRun = latest?.next_run_at || (latest?.completed_at
      ? new Date(new Date(latest.completed_at).getTime() + getFreqHours(config) * 60 * 60 * 1000).toISOString()
      : null);
    const missed = Boolean(enabled && nextRun && new Date(nextRun).getTime() < Date.now() - 15 * 60 * 1000 && normalizeStatus(latest?.status || "") !== "running");
    const health = getLatestHealth(latest, enabled, Boolean(stalled), missed);
    return { site, latest, enabled, stalled: Boolean(stalled), missed, health, progress, nextRun };
  }), [automationWebsites, automationJobs]);

  const backlogByDay = useMemo(() => {
    const days = Array.from({ length: 7 }).map((_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - index));
      const key = format(date, "yyyy-MM-dd");
      const count = automationJobs.filter((j) => format(new Date(j.created_at), "yyyy-MM-dd") === key && normalizeStatus(j.status) === "queued").length;
      return { key, label: format(date, "EEE"), count };
    });
    const max = Math.max(1, ...days.map((d) => d.count));
    return days.map((d) => ({ ...d, height: Math.max(8, (d.count / max) * 48) }));
  }, [automationJobs]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["websites-autopublish", currentCompanyId] });
    queryClient.invalidateQueries({ queryKey: ["autopublish-jobs", currentCompanyId] });
    queryClient.invalidateQueries({ queryKey: ["autopublish-job-runs", currentCompanyId] });
  };

  const runEngine = async (mode: "tick" | "drain" = "tick") => {
    if (trialMode.isReadOnly) {
      blockTrialAction();
      return;
    }
    setRunningEngine(true);
    try {
      const { data, error } = await supabase.functions.invoke("autopublish-engine", { body: { mode, limit: 8, company_id: currentCompanyId } });
      if (error) throw error;
      if ((data as any)?.fallback || (data as any)?.success === false) {
        toast.warning("Automation is temporarily unavailable", { description: (data as any)?.message || "Please retry in a moment." });
        return;
      }
      refreshAll();
      toast.success("Automation engine checked", { description: `${data?.processed || 0} job(s) processed.` });
    } catch (error: any) {
      toast.error("Automation could not run", { description: "Please retry in a moment." });
    } finally {
      setRunningEngine(false);
    }
  };

  const toggleSiteAuto = async (siteId: string, enabled: boolean) => {
    if (trialMode.isReadOnly) {
      blockTrialAction();
      return;
    }
    const site = getSite(siteId);
    const currentConfig = site?.automation_config || {};
    const { error } = await tenantDb
        .from("websites")
        .update({ automation_config: { ...currentConfig, enabled, autoPublish: enabled } as any })
      .eq("id", siteId)
      .eq("company_id", currentCompanyId);
    if (error) {
      toast.error("Automation setting was not saved", { description: "Please retry from this row." });
      return;
    }
    refreshAll();
    toast.success(enabled ? "Automation resumed" : "Automation paused");
  };

  const updateIntervalHours = async (siteId: string, hours: number) => {
    if (trialMode.isReadOnly) {
      blockTrialAction();
      return;
    }
    if (!Number.isFinite(hours) || hours <= 0) {
      toast.error("Schedule was not updated", { description: "Enter an interval greater than 0 hours." });
      return;
    }
    const site = getSite(siteId);
    const currentConfig = site?.automation_config || {};
    const normalizedHours = Math.round(hours * 100) / 100;
    const preset = FREQ_OPTIONS.find((f) => f.hours === normalizedHours);
    const frequency = preset?.value || `${normalizedHours}h`;
    const { error } = await tenantDb
        .from("websites")
        .update({ automation_config: { ...currentConfig, frequency, intervalHours: normalizedHours } as any })
      .eq("id", siteId)
      .eq("company_id", currentCompanyId);
    if (error) {
      toast.error("Schedule was not updated", { description: "The previous schedule is still active." });
      return;
    }
    refreshAll();
    toast.success("Schedule updated");
  };

  const updatePublishWithoutAi = async (siteId: string, publishWithoutAiRewrite: boolean) => {
    if (trialMode.isReadOnly) {
      blockTrialAction();
      return;
    }
    const site = getSite(siteId);
    const currentConfig = site?.automation_config || {};
    const { error } = await tenantDb
      .from("websites")
      .update({ automation_config: { ...currentConfig, publishWithoutAiRewrite } as any })
      .eq("id", siteId)
      .eq("company_id", currentCompanyId);
    if (error) {
      toast.error("Credit mode was not saved", { description: "The previous automation mode is still active." });
      return;
    }
    refreshAll();
    toast.success(publishWithoutAiRewrite ? "Automation will publish reviewed articles without AI credits" : "Automation will generate/rewrite articles and consume AI credits");
  };

  const pauseAll = async () => {
    if (trialMode.isReadOnly) {
      blockTrialAction();
      return;
    }
    setPausingAll(true);
    try {
      await Promise.all(automationWebsites.map((site) => {
        const currentConfig = site.automation_config || {};
        return tenantDb
          .from("websites")
          .update({ automation_config: { ...currentConfig, enabled: false, autoPublish: false } as any })
          .eq("id", site.id)
          .eq("company_id", currentCompanyId);
      }));
      refreshAll();
      toast.success("All automation paused");
    } catch {
      toast.error("Some jobs could not be paused", { description: "Refresh and retry the remaining active sites." });
    } finally {
      setPausingAll(false);
    }
  };

  const triggerNow = async (siteId: string) => {
    if (trialMode.isReadOnly) {
      blockTrialAction();
      return;
    }
    if (!user) return;
    setTriggeringSite(siteId);
    try {
      const site = getSite(siteId);
      const { error } = await tenantDb.from("autopublish_jobs").insert({
        website_id: siteId,
        company_id: currentCompanyId,
        status: "queued",
        scheduled_for: new Date().toISOString(),
        trigger_type: "manual",
        input_data: {
          site_name: site?.name,
          automation_config: {
            ...(site?.automation_config || {}),
            publishWithoutAiRewrite: site?.automation_config?.publishWithoutAiRewrite !== false,
          },
          content_config: site?.content_config || {},
          publish_without_ai_rewrite: site?.automation_config?.publishWithoutAiRewrite !== false,
          estimated_credits: site?.automation_config?.publishWithoutAiRewrite === false ? "rewrite_model_cost" : 0,
          credit_reason: site?.automation_config?.publishWithoutAiRewrite === false
            ? "AI rewrite/generation is enabled for this manual automation run"
            : "Publishing an existing reviewed article does not consume AI credits",
        },
      } as any);
      if (error) throw error;
      await runEngine("drain");
      toast.success("Manual run started");
    } catch (error: any) {
      toast.error("Job could not start", { description: error?.message || "The job remains unchanged." });
    } finally {
      setTriggeringSite(null);
    }
  };

  const retryJob = async (job: JobRow) => {
    if (trialMode.isReadOnly) {
      blockTrialAction();
      return;
    }
    setRetryingJob(job.id);
    try {
      const { error } = await tenantDb
        .from("autopublish_jobs")
        .update({ status: "queued", scheduled_for: new Date().toISOString(), error_message: null, health: "healthy", progress: 0 } as any)
        .eq("id", job.id)
        .eq("company_id", currentCompanyId);
      if (error) throw error;
      await runEngine("drain");
      toast.success("Job retry started");
    } catch (error: any) {
      toast.error("Retry could not start", { description: error?.message || "Please try again." });
    } finally {
      setRetryingJob(null);
    }
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const meta = statusMeta(status);
    const Icon = meta.icon;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${meta.className}`}>
        <Icon className={`h-3 w-3 ${normalizeStatus(status) === "running" ? "animate-spin" : ""}`} />
        {meta.label}
      </span>
    );
  };

  const HealthBadge = ({ health }: { health: string }) => {
    const meta = health === "healthy"
      ? { classes: "bg-success/10 text-success", icon: ShieldCheck, label: "healthy" }
      : health === "failed"
        ? { classes: "bg-destructive/10 text-destructive", icon: XCircle, label: "failed" }
        : health === "running"
          ? { classes: "bg-info/10 text-info", icon: Loader2, label: "running" }
          : health === "queued"
            ? { classes: "bg-warning/10 text-warning", icon: Clock, label: "queued" }
            : health === "paused"
              ? { classes: "bg-muted text-muted-foreground", icon: Pause, label: "paused" }
              : health === "idle"
                ? { classes: "bg-muted text-muted-foreground", icon: Clock, label: "idle" }
                : { classes: "bg-warning/10 text-warning", icon: AlertTriangle, label: "warning" };
    const Icon = meta.icon;
    return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${meta.classes}`}><Icon className={`h-3 w-3 ${health === "running" ? "animate-spin" : ""}`} />{meta.label}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <Zap className="h-6 w-6 text-primary" />
            Auto-Publishing Command Center
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Reliable publishing automation, live monitoring, execution history, and health alerts.</p>
          {trialMode.isReadOnly && <p className="mt-1 text-xs font-medium text-amber-700">Demo data. Automation controls are read-only in Trial mode.</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={refreshAll}>
            <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => runEngine("tick")} disabled={runningEngine || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
            {runningEngine ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Activity className="mr-1.5 h-4 w-4" />}
            Run Engine
          </Button>
          <Button variant="destructive" size="sm" onClick={pauseAll} disabled={pausingAll || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
            {pausingAll ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Pause className="mr-1.5 h-4 w-4" />}
            Pause All
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" />
          <div className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Article selection and publishing rules</h2>
              <p className="mt-1 text-xs text-muted-foreground">Automation only publishes articles that pass the eligibility checks for the selected destination.</p>
            </div>
            <div className="grid gap-3 text-xs text-muted-foreground md:grid-cols-4">
              <div><span className="font-semibold text-foreground">Eligible article</span><p className="mt-1">Has a title, usable body content, a valid target website, and an assigned editorial author.</p></div>
              <div><span className="font-semibold text-foreground">Duplicate prevention</span><p className="mt-1">An article already recorded as published for a website is excluded for that same website.</p></div>
              <div><span className="font-semibold text-foreground">Hidden reasons</span><p className="mt-1">Articles may not appear when already published, missing content, missing author data, or lacking publishing credentials.</p></div>
              <div><span className="font-semibold text-foreground">Source of truth</span><p className="mt-1">The Published Articles page records every successful destination and controls whether an article becomes available again after deletion.</p></div>
            </div>
          </div>
        </div>
      </div>

      {(stats.stopped || stats.oldQueued.length > 0 || stats.stalled.length > 0) && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
            <div>
              <h2 className="text-sm font-semibold text-destructive">Automation needs attention</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {stats.stopped
                  ? `No automation job has completed since ${stats.lastRunAt ? format(new Date(stats.lastRunAt), "MMM d, HH:mm") : "the last check"}.`
                  : `${stats.oldQueued.length + stats.stalled.length} job(s) missed their expected execution window.`}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <div className="stat-card"><p className="text-xs text-muted-foreground">Executed</p><p className="mt-1 text-2xl font-bold">{stats.executed}</p></div>
        <div className="stat-card"><p className="text-xs text-muted-foreground">Success Rate</p><p className="mt-1 text-2xl font-bold text-success">{stats.successRate}%</p></div>
        <div className="stat-card"><p className="text-xs text-muted-foreground">Avg Duration</p><p className="mt-1 text-2xl font-bold">{formatDuration(stats.avgDuration)}</p></div>
        <div className="stat-card"><p className="text-xs text-muted-foreground">Queue</p><p className="mt-1 text-2xl font-bold text-warning">{stats.queued.length}</p></div>
        <div className="stat-card"><p className="text-xs text-muted-foreground">Running</p><p className="mt-1 text-2xl font-bold text-info">{stats.running.length}</p></div>
        <div className="stat-card"><p className="text-xs text-muted-foreground">Alerts</p><p className="mt-1 text-2xl font-bold text-destructive">{stats.stalled.length + stats.repeatedFailures.length}</p></div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_0.6fr]">
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold">Website Automation Controls</h2></div>
            <span className="text-xs text-muted-foreground">{automationWebsites.length} sites{trialMode.isReadOnly ? " • demo" : ""}</span>
          </div>
          {loadingWebsites ? (
            <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : automationWebsites.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No websites configured yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {siteSummaries.map(({ site, latest, enabled, health, progress, nextRun, stalled, missed }) => {
                const config = site.automation_config || {};
                const intervalHours = getFreqHours(config);
                const presetValue = customFrequencySites.has(site.id)
                  ? "custom"
                  : FREQ_OPTIONS.find((f) => f.hours === intervalHours)?.value || "custom";
                return (
                  <div key={site.id} className="grid gap-3 px-4 py-3 lg:grid-cols-[1.2fr_0.9fr_0.7fr_1fr_auto] lg:items-center">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-foreground">{site.name}</p>
                        <HealthBadge health={health} />
                        {stalled && <span className="text-[10px] font-semibold text-destructive">STALLED</span>}
                        {missed && <span className="text-[10px] font-semibold text-destructive">MISSED</span>}
                      </div>
                      <p className="mt-1 truncate text-[11px] text-muted-foreground">{site.content_config?.niche || "No niche"} • {site.domain || "No domain"}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <span><span className="text-muted-foreground">Last:</span> {latest?.last_run_at || latest?.created_at ? formatDistanceToNow(new Date(latest.last_run_at || latest.created_at), { addSuffix: true }) : "—"}</span>
                      <span><span className="text-muted-foreground">Next:</span> {nextRun ? formatDistanceToNow(new Date(nextRun), { addSuffix: true }) : "—"}</span>
                      <span><span className="text-muted-foreground">Duration:</span> {formatDuration(latest?.duration_ms)}</span>
                      <span><span className="text-muted-foreground">Attempts:</span> {latest?.attempts || 0}/{latest?.max_attempts || 3}</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground"><span>Progress</span><span>{progress}%</span></div>
                      <Progress value={progress} className="h-1.5" />
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_88px] gap-2">
                      <Select
                        value={presetValue}
                        onValueChange={(value) => {
                          if (value === "custom") {
                            setCustomFrequencySites((prev) => new Set(prev).add(site.id));
                            return;
                          }
                          const option = FREQ_OPTIONS.find((f) => f.value === value);
                          if (option) {
                            setCustomFrequencySites((prev) => {
                              const next = new Set(prev);
                              next.delete(site.id);
                              return next;
                            });
                            updateIntervalHours(site.id, option.hours);
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FREQ_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>)}
                          <SelectItem value="custom" className="text-xs">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="relative">
                        <Input
                          key={`${site.id}-${intervalHours}`}
                          type="number"
                          min="0.25"
                          step="0.25"
                          defaultValue={intervalHours}
                          onBlur={(event) => updateIntervalHours(site.id, Number(event.currentTarget.value))}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.currentTarget.blur();
                            }
                          }}
                          className="h-8 pr-7 text-xs"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">h</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <label className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-[10px] text-muted-foreground">
                        <Switch
                          checked={config.publishWithoutAiRewrite !== false}
                          onCheckedChange={(value) => updatePublishWithoutAi(site.id, value)}
                          disabled={trialMode.isReadOnly}
                        />
                        <span>{config.publishWithoutAiRewrite === false ? "AI rewrite credits" : "0-credit publish"}</span>
                      </label>
                      <Switch checked={enabled} onCheckedChange={(value) => toggleSiteAuto(site.id, value)} disabled={trialMode.isReadOnly} />
                      <Button variant="outline" size="sm" onClick={() => triggerNow(site.id)} disabled={triggeringSite === site.id || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                        {triggeringSite === site.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold">Backlog Trend</h2></div>
          <div className="flex h-20 items-end gap-2 border-b border-border pb-2">
            {backlogByDay.map((day) => <div key={day.key} className="flex flex-1 flex-col items-center gap-1"><div className="w-full rounded-sm bg-warning/30" style={{ height: day.height }} /><span className="text-[10px] text-muted-foreground">{day.label}</span></div>)}
          </div>
          <div className="mt-4 space-y-2 text-xs">
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Failed runs</span><span className="font-semibold text-destructive">{stats.failed.length}</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Stalled jobs</span><span className="font-semibold text-destructive">{stats.stalled.length}</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Repeated failures</span><span className="font-semibold text-warning">{stats.repeatedFailures.length}</span></div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="jobs" className="rounded-lg border border-border bg-card">
        <TabsList className="h-auto w-full justify-start rounded-none border-b border-border bg-transparent px-2 py-1">
          <TabsTrigger value="jobs" className="text-xs"><Settings2 className="mr-1.5 h-3.5 w-3.5" /> Jobs</TabsTrigger>
          <TabsTrigger value="history" className="text-xs"><Timer className="mr-1.5 h-3.5 w-3.5" /> Execution History</TabsTrigger>
          <TabsTrigger value="alerts" className="text-xs"><Gauge className="mr-1.5 h-3.5 w-3.5" /> Health Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="m-0">
          {loadingJobs ? <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
            <div className="divide-y divide-border">
              {automationJobs.map((job) => (
                <div key={job.id} className="grid gap-3 px-4 py-3 lg:grid-cols-[1fr_0.55fr_0.55fr_0.55fr_1fr_auto] lg:items-center">
                  <div className="min-w-0"><p className="truncate text-xs font-semibold">{getSiteName(job.website_id)}</p><p className="truncate text-[10px] text-muted-foreground">Job {job.id.slice(0, 8)} • {job.trigger_type || "manual"}</p></div>
                  <StatusBadge status={job.status} />
                  <span className="text-[11px] text-muted-foreground">{format(new Date(job.scheduled_for), "MMM d, HH:mm")}</span>
                  <span className="text-[11px] text-muted-foreground">{formatDuration(job.duration_ms)}</span>
                  <div className="min-w-0 text-[10px] text-muted-foreground">{job.error_message ? <span className="text-destructive">{job.error_message}</span> : `Next ${job.next_run_at ? formatDistanceToNow(new Date(job.next_run_at), { addSuffix: true }) : "—"}`}</div>
                  <Button variant="outline" size="sm" onClick={() => retryJob(job)} disabled={retryingJob === job.id || normalizeStatus(job.status) === "running" || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                    {retryingJob === job.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="m-0">
          <div className="grid gap-2 border-b border-border p-3 md:grid-cols-[160px_1fr_150px_150px]">
            <Select value={runStatusFilter} onValueChange={setRunStatusFilter}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="running">Running</SelectItem><SelectItem value="failed">Failed</SelectItem></SelectContent></Select>
            <Select value={jobFilter} onValueChange={setJobFilter}><SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All jobs" /></SelectTrigger><SelectContent><SelectItem value="all">All jobs</SelectItem>{automationJobs.map((j) => <SelectItem key={j.id} value={j.id}>{getSiteName(j.website_id)} • {j.id.slice(0, 6)}</SelectItem>)}</SelectContent></Select>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-8 text-xs" />
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-8 text-xs" />
          </div>
          {loadingRuns ? <div className="p-4"><Skeleton className="h-24 w-full" /></div> : (
            <div className="divide-y divide-border max-h-[460px] overflow-y-auto">
              {runs.map((run) => (
                <div key={run.id} className="grid gap-3 px-4 py-3 lg:grid-cols-[0.8fr_0.6fr_0.6fr_1fr_1fr]">
                  <div><p className="text-xs font-semibold">{getSiteName(run.website_id)}</p><p className="text-[10px] text-muted-foreground">Attempt {run.attempt} • {run.trigger_type}</p></div>
                  <StatusBadge status={run.status} />
                  <span className="text-[11px] text-muted-foreground">{formatDuration(run.duration_ms)}</span>
                  <p className="truncate text-[10px] text-muted-foreground">Input: {JSON.stringify(run.input_data || {}).slice(0, 140)}</p>
                  <p className={`truncate text-[10px] ${run.error_message ? "text-destructive" : "text-muted-foreground"}`}>{run.error_message || `Result: ${JSON.stringify(run.output_data || {}).slice(0, 140)}`}</p>
                </div>
              ))}
              {runs.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No execution history matches these filters.</p>}
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="m-0 divide-y divide-border">
          {[...stats.oldQueued.map((job) => ({ key: job.id, title: `${getSiteName(job.website_id)} missed its schedule`, detail: `Scheduled for ${format(new Date(job.scheduled_for), "MMM d, HH:mm")}. Run the engine or retry the job.`, severity: "failed" })), ...stats.stalled.map((job) => ({ key: job.id, title: `${getSiteName(job.website_id)} is stalled`, detail: job.error_message || "The job has been running for more than 20 minutes.", severity: "failed" })), ...stats.repeatedFailures.map((site) => ({ key: site.id, title: `${site.name} has repeated failures`, detail: "Two or more recent jobs failed. Check connection settings and logs.", severity: "warning" }))].map((alert) => (
            <div key={alert.key} className="flex items-start gap-3 px-4 py-3"><AlertTriangle className={`mt-0.5 h-4 w-4 ${alert.severity === "failed" ? "text-destructive" : "text-warning"}`} /><div><p className="text-sm font-semibold">{alert.title}</p><p className="text-xs text-muted-foreground">{alert.detail}</p></div></div>
          ))}
          {stats.oldQueued.length + stats.stalled.length + stats.repeatedFailures.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">All automation jobs are healthy.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
